import React from 'react';

// ─── Metric Card Skeleton ──────────────────────────────────────────────────
export const CommonCardSkeleton: React.FC = () => {
    return (
        <div className="p-5 bg-card border border-border rounded-xl shadow-sm animate-pulse flex items-center justify-between gap-4">
            <div className="space-y-2 flex-1">
                <div className="h-3 bg-zinc-200 rounded-md w-24" />
                <div className="h-7 bg-zinc-300 rounded-md w-16" />
            </div>
            <div className="w-12 h-12 rounded-xl bg-zinc-200/80 shrink-0" />
        </div>
    );
};

// ─── Line Chart Skeleton ───────────────────────────────────────────────────
export const LineChartSkeleton: React.FC = () => (
    <div className="h-64 w-full pt-2 flex flex-col justify-between animate-pulse">
        <div className="relative w-full h-48 flex gap-3">
            {/* Y-Axis Labels */}
            <div className="flex flex-col justify-between h-full py-1 text-right w-10 shrink-0 pr-1">
                <div className="h-2.5 bg-zinc-200 rounded w-8 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-6 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-8 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-5 ml-auto" />
            </div>
            {/* Chart Area */}
            <div className="relative flex-1 h-full border-b border-l border-border/60">
                {/* Horizontal Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-30 py-1">
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                </div>
                {/* Pulsing Line Path */}
                <svg className="absolute inset-0 w-full h-full text-zinc-300/70" viewBox="0 0 100 50" preserveAspectRatio="none">
                    <path
                        d="M 0 40 C 15 25, 25 35, 40 15 C 55 10, 65 30, 80 20 L 100 5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                    />
                </svg>
            </div>
        </div>
        {/* X-Axis Labels */}
        <div className="flex justify-between pl-12 pr-2 pt-2">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-2.5 bg-zinc-200 rounded-md w-10" />
            ))}
        </div>
    </div>
);

// ─── Bar Chart Skeleton ────────────────────────────────────────────────────
export const BarChartSkeleton: React.FC = () => (
    <div className="h-64 w-full pt-2 flex flex-col justify-between animate-pulse">
        <div className="relative w-full h-48 flex gap-3">
            {/* Y-Axis Labels */}
            <div className="flex flex-col justify-between h-full py-1 text-right w-10 shrink-0 pr-1">
                <div className="h-2.5 bg-zinc-200 rounded w-8 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-6 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-8 ml-auto" />
                <div className="h-2.5 bg-zinc-200 rounded w-5 ml-auto" />
            </div>
            {/* Chart Area */}
            <div className="relative flex-1 h-full border-b border-l border-border/60 flex items-end justify-around px-4">
                {/* Horizontal Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-30 py-1">
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                    <div className="border-t border-dashed border-zinc-300" />
                </div>
                {/* Pulsing Columns */}
                {[45, 75, 55, 90, 60, 80, 50, 85, 40, 70].map((height, i) => (
                    <div
                        key={i}
                        className="bg-zinc-200/80 hover:bg-zinc-300 rounded-t-md w-[6%] transition-all"
                        style={{ height: `${height}%` }}
                    />
                ))}
            </div>
        </div>
        {/* X-Axis Labels */}
        <div className="flex justify-between pl-12 pr-2 pt-2">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-2.5 bg-zinc-200 rounded-md w-10" />
            ))}
        </div>
    </div>
);

// ─── Horizontal Bar Chart Skeleton ──────────────────────────────────────────
export const HorizontalBarChartSkeleton: React.FC = () => (
    <div className="h-64 w-full pt-2 flex flex-col justify-between animate-pulse">
        <div className="relative w-full h-48 flex gap-3">
            {/* Category Names Labels (Store Names) */}
            <div className="flex flex-col justify-around h-full py-1 text-right w-20 shrink-0 pr-1 gap-2">
                <div className="h-3 bg-zinc-200 rounded w-16 ml-auto" />
                <div className="h-3 bg-zinc-200 rounded w-12 ml-auto" />
                <div className="h-3 bg-zinc-200 rounded w-14 ml-auto" />
                <div className="h-3 bg-zinc-200 rounded w-10 ml-auto" />
                <div className="h-3 bg-zinc-200 rounded w-16 ml-auto" />
            </div>
            {/* Chart Area */}
            <div className="relative flex-1 h-full border-b border-l border-border/60 flex flex-col justify-around py-2">
                {/* Vertical Grid lines */}
                <div className="absolute inset-0 flex justify-between pointer-events-none opacity-30">
                    <div className="border-l border-dashed border-zinc-300 h-full" />
                    <div className="border-l border-dashed border-zinc-300 h-full" />
                    <div className="border-l border-dashed border-zinc-300 h-full" />
                    <div className="border-l border-dashed border-zinc-300 h-full" />
                </div>
                {/* Pulsing Horizontal Bars */}
                {[85, 60, 75, 45, 55].map((width, i) => (
                    <div key={i} className="w-full pl-2">
                        <div
                            className="bg-zinc-200/80 rounded-r-md h-4"
                            style={{ width: `${width}%` }}
                        />
                    </div>
                ))}
            </div>
        </div>
        {/* X-Axis Labels */}
        <div className="flex justify-between pl-24 pr-2 pt-2">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-2.5 bg-zinc-200 rounded-md w-8" />
            ))}
        </div>
    </div>
);

// ─── Donut Chart Skeleton ──────────────────────────────────────────────────
export const DonutChartSkeleton: React.FC = () => (
    <div className="h-64 flex flex-col items-center justify-center gap-6 animate-pulse">
        <div className="relative w-36 h-36 rounded-full border-[18px] border-zinc-200/60 flex items-center justify-center">
            {/* Center label placeholder */}
            <div className="text-center space-y-1.5">
                <div className="h-3 bg-zinc-200 rounded-md w-14 mx-auto" />
                <div className="h-5 bg-zinc-300 rounded-md w-10 mx-auto" />
            </div>
        </div>
        {/* Legends */}
        <div className="flex flex-wrap justify-center gap-4">
            {['Delivered', 'In Transit', 'Cancelled', 'Failed'].map((label, i) => (
                <div key={i} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <div className="h-3 bg-zinc-200 rounded-md w-12" />
                </div>
            ))}
        </div>
    </div>
);

// ─── Funnel Chart Skeleton ──────────────────────────────────────────────────
export const FunnelChartSkeleton: React.FC = () => (
    <div className="h-64 pt-4 space-y-3.5 overflow-hidden animate-pulse">
        {[100, 80, 60, 40, 20].map((width, i) => (
            <div key={i} className="flex items-center gap-2">
                {/* Stage Label Skeleton */}
                <div className="h-3.5 bg-zinc-200 rounded w-20 shrink-0 ml-auto" />
                {/* Funnel Stage Bar Skeleton */}
                <div className="flex-1 h-7 bg-zinc-200/40 rounded-md overflow-hidden">
                    <div
                        className="h-full rounded-md bg-zinc-200"
                        style={{ width: `${width}%` }}
                    />
                </div>
            </div>
        ))}
    </div>
);

// ─── Recent Orders Sidebar Skeleton ────────────────────────────────────────
export const RecentOrdersSkeleton: React.FC = () => (
    <div className="space-y-3 animate-pulse">
        {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-zinc-50">
                <div className="space-y-2 flex-1">
                    {/* Order ID */}
                    <div className="h-4 bg-zinc-200 rounded-md w-16" />
                    {/* Customer name */}
                    <div className="h-3.5 bg-zinc-200 rounded-md w-24" />
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    {/* Amount */}
                    <div className="h-4 bg-zinc-200 rounded-md w-10" />
                    {/* Status Badge */}
                    <div className="h-5 bg-zinc-200 rounded-full w-16" />
                </div>
            </div>
        ))}
    </div>
);
